import { ChangeDetectionStrategy, Component, computed, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { NgTemplateOutlet } from "@angular/common";
import { form, FormField, required, submit } from "@angular/forms/signals";
import { S25Util } from "../../util/s25-util";
import { AuthService } from "../../services/auth.service";
import { StateService } from "../../services/state.service";
import { S25States } from "../../services/state/state.map";
import { S25LoadingInlineStaticComponent } from "../s25-loading/s25.loading.inline.static.component";
import { FormsModule } from "@angular/forms";
import { MoreMenuUtil } from "../s25-nav-bar/more-menu/more.menu.util";
import { PasswordResetService } from "../../services/password.reset.service";
import { PreferenceService } from "../../services/preference.service";
import { s25Resource } from "../../util/s25.resource";
import { Bind } from "../../decorators/bind.decorator";

@TypeManagerDecorator("s25-ng-se-login")
@Component({
    selector: "s25-ng-se-login",
    template: `
        <div class="cn-login" data-screen-label="Connect25 Login">
            <aside class="cn-login__brand">
                <div class="cn-login__brand-inner">
                    <img
                        src="./resources/typescript/assets/images/connect25.png"
                        alt="connect25 — Powered by CollegeNET"
                        class="cn-login__logo"
                    />
                    <h1 class="cn-login__tagline">
                        Find your people.
                        <span class="cn-login__accent">Find your thing.</span>
                    </h1>
                    <p class="cn-login__sub">
                        Discover events, clubs, and free food across campus — all in one place. Sign in with your school
                        account to get started.
                    </p>
                    <ul class="cn-login__bullets">
                        <li>Browse what's happening on campus today</li>
                        <li>RSVP and track your registrations</li>
                        <li>Check in with a swipe to earn rewards</li>
                    </ul>

                    <ng-container [ngTemplateOutlet]="loginFormRef" [ngTemplateOutletContext]="{ view: 'mobile' }" />

                    <div class="cn-login__peek" aria-hidden="true">
                        <div class="cn-login__peek-card">
                            <div class="cn-login__peek-date">
                                <span class="cn-login__peek-mo">OCT</span>
                                <span class="cn-login__peek-day">12</span>
                            </div>
                            <div class="cn-login__peek-body">
                                <div class="cn-login__peek-title">Fall Block Party</div>
                                <div class="cn-login__peek-meta">Memorial Union Lawn · 6:00 PM</div>
                                <div class="cn-login__peek-tags"><span>Free food</span><span>Music</span></div>
                            </div>
                        </div>
                        <div class="cn-login__peek-card cn-login__peek-card--alt">
                            <div class="cn-login__peek-date cn-login__peek-date--alt">
                                <span class="cn-login__peek-mo">OCT</span>
                                <span class="cn-login__peek-day">14</span>
                            </div>
                            <div class="cn-login__peek-body">
                                <div class="cn-login__peek-title">Climbing Club Open House</div>
                                <div class="cn-login__peek-meta">Rec Center · 7:30 PM</div>
                            </div>
                        </div>
                    </div>
                </div>
                <footer class="cn-login__brandfoot">connect25 · Powered by CollegeNET</footer>
            </aside>

            <ng-container [ngTemplateOutlet]="loginFormRef" [ngTemplateOutletContext]="{ view: 'desktop' }" />
        </div>

        <ng-template #loginFormRef let-viewType="view">
            <main class="cn-login__form cn-login__form--{{ viewType }}">
                <div class="cn-login__form-bg" aria-hidden="true"></div>
                <div class="cn-login__form-inner">
                    <div class="cn-login__mark" aria-hidden="true">
                        <s25-ng-icon [type]="'calendarDateRounded'" [size]="22" [ariaLabel]="false" />
                    </div>
                    <header class="cn-login__head">
                        <h2>Welcome to Connect25</h2>
                        <p>Sign in with your school account to see what's happening on campus.</p>
                    </header>

                    <form (ngSubmit)="logIn()">
                        @let userField = loginForm.username();
                        <label class="cn-field" [class.cn-field__error]="userField.touched() && userField.invalid()">
                            <span class="cn-field__label">Username</span>
                            <span class="cn-field__wrap">
                                <s25-ng-icon [type]="'user'" class="cn-field__icon" [size]="16" [ariaLabel]="false" />
                                <input [formField]="loginForm.username" />
                            </span>
                        </label>
                        @if (userField.invalid() && userField.touched()) {
                            <p class="ngRed c-margin-bottom--half">{{ userField.errors()?.[0]?.message }}</p>
                        }

                        @let pwField = loginForm.password();
                        <label class="cn-field" [class.cn-field__error]="pwField.touched() && pwField.invalid()">
                            <span class="cn-field__label"> Password </span>
                            <span class="cn-field__wrap cn-field__pwwrap">
                                <s25-ng-icon [type]="'lock'" class="cn-field__icon" [size]="16" [ariaLabel]="false" />
                                <input
                                    [type]="showPw() ? 'text' : 'password'"
                                    name="password"
                                    placeholder="••••••••"
                                    [formField]="loginForm.password"
                                />
                                <button type="button" class="cn-field__toggle" (click)="showPw.set(!showPw())">
                                    {{ showPw() ? "Hide" : "Show" }}
                                </button>
                            </span>
                        </label>
                        @if (pwField.invalid() && pwField.touched()) {
                            <p class="ngRed c-margin-bottom--half">{{ pwField.errors()?.[0]?.message }}</p>
                        }

                        @if (showForgotPassword.value()) {
                            <button
                                type="button"
                                class="cn-field__link cn-login__forgot"
                                [disabled]="!loginForm.username().valid() || requestingReset()"
                                (click)="forgotPassword()"
                            >
                                Forgot your Password?
                            </button>
                        }

                        @if (forgotPasswordResult()) {
                            <p
                                class="cn-login__forgot-result"
                                [class.cn-login__forgot-result--error]="!forgotPasswordResult().success"
                            >
                                {{ forgotPasswordResult().message }}
                            </p>
                        }

                        @if (loginError()) {
                            <p class="ngRed">{{ loginError() }}</p>
                        }

                        <button type="submit" class="cn-btn cn-btn--primary" [disabled]="loginForm().submitting()">
                            @if (loginForm().submitting()) {
                                <s25-ng-loading-inline-static />
                            } @else {
                                Sign in
                                <s25-ng-icon [type]="'arrowRight'" [ariaLabel]="false" />
                            }
                        </button>
                    </form>
                </div>
            </main>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        :host {
            display: block;
            height: 100vh;
            overflow: hidden;
            color: var(--cn-fg-1);
        }

        .cn-login {
            display: grid;
            grid-template-columns: 1fr 1fr;
            height: 100%;
        }

        .cn-login__form--mobile {
            display: none !important;
        }

        @media (max-width: 890px) {
            :host {
                height: auto;
                min-height: 100vh;
                overflow: visible;
            }

            .cn-login {
                grid-template-columns: 1fr;
                height: auto;
                min-height: 100vh;
            }

            .cn-login__form--desktop {
                display: none !important;
            }

            .cn-login__form--mobile {
                display: flex !important;
                margin-top: 32px;
            }

            .cn-login__peek {
                display: none;
            }

            .cn-login__brand {
                min-height: 100vh;
                height: auto;
            }
        }

        .cn-login__brand {
            background: linear-gradient(160deg, var(--cn-navy) 0%, var(--cn-navy-700) 100%);
            color: #fff;
            padding: 56px 56px 32px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            position: relative;
            overflow: hidden;
        }

        .cn-login__brand::after {
            content: "";
            position: absolute;
            right: -120px;
            bottom: -120px;
            width: 380px;
            height: 380px;
            border-radius: 50%;
            background: var(--cn-orange);
            opacity: 0.12;
            filter: blur(8px);
        }

        .cn-login__brand-inner {
            position: relative;
            z-index: 1;
            max-width: 460px;
        }

        .cn-login__logo {
            height: auto;
            max-width: 280px;
            background: #fff;
            padding: 16px 20px;
            border-radius: 12px;
        }

        .cn-login__tagline {
            font-weight: 800;
            font-size: 40px;
            line-height: 1.15;
            letter-spacing: -0.02em;
            margin: 40px 0 16px;
        }

        .cn-login__accent {
            color: var(--cn-orange);
        }

        .cn-login__sub {
            font-size: 16px;
            line-height: 1.55;
            color: #cdd6e6;
            margin: 0 0 28px;
        }

        .cn-login__bullets {
            list-style: none;
            padding: 0;
            margin: 0;
            display: grid;
            gap: 10px;
        }

        .cn-login__bullets li {
            position: relative;
            padding-left: 26px;
            font-size: 14.5px;
            color: #e3eaf5;
        }

        .cn-login__bullets li::before {
            content: "";
            position: absolute;
            left: 0;
            top: 7px;
            width: 14px;
            height: 8px;
            border-left: 2px solid var(--cn-orange);
            border-bottom: 2px solid var(--cn-orange);
            transform: rotate(-45deg);
        }

        .cn-login__peek {
            margin-top: 36px;
            position: relative;
            height: 140px;
        }

        .cn-login__peek-card {
            position: absolute;
            left: 0;
            top: 0;
            width: 320px;
            background: #fff;
            color: var(--cn-fg-1);
            border-radius: 12px;
            padding: 14px;
            display: flex;
            gap: 14px;
            box-shadow: 0 18px 40px -12px rgba(0, 0, 0, 0.4);
            transform: rotate(-2deg);
        }

        .cn-login__peek-card--alt {
            left: 80px;
            top: 56px;
            width: 290px;
            transform: rotate(3deg);
            opacity: 0.92;
        }

        .cn-login__peek-date {
            flex: 0 0 56px;
            height: 60px;
            border-radius: 8px;
            background: var(--cn-orange);
            color: #fff;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            font-family: var(--font--header), sans-serif;
        }

        .cn-login__peek-date--alt {
            background: var(--cn-navy);
        }

        .cn-login__peek-mo {
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.08em;
        }

        .cn-login__peek-day {
            font-size: 22px;
            font-weight: 800;
            line-height: 1;
        }

        .cn-login__peek-body {
            flex: 1;
            min-width: 0;
        }

        .cn-login__peek-title {
            font-family: var(--font--header), sans-serif;
            font-weight: 700;
            font-size: 15px;
            color: var(--cn-fg-1);
            margin-bottom: 4px;
        }

        .cn-login__peek-meta {
            font-size: 12px;
            color: var(--cn-fg-3);
            margin-bottom: 8px;
        }

        .cn-login__peek-tags {
            display: flex;
            gap: 6px;
        }

        .cn-login__peek-tags span {
            font-size: 10.5px;
            font-weight: 600;
            padding: 3px 8px;
            border-radius: 999px;
            background: #f0e6d0;
            color: #8a5a00;
        }

        .cn-login__brandfoot {
            font-size: 12px;
            color: #a8b5d1;
            position: relative;
            z-index: 1;
        }

        .cn-login__form {
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;

            &.cn-login__form--desktop {
                background: var(--cn-bg-alt);
                padding: 56px;
                overflow: hidden;
            }
        }

        .cn-login__form-bg {
            position: absolute;
            inset: 0;
            pointer-events: none;
            background-image: radial-gradient(rgba(31, 68, 134, 0.1) 1px, transparent 1px);
            background-size: 22px 22px;
            mask-image: radial-gradient(ellipse at 50% 50%, #000 40%, transparent 80%);
            -webkit-mask-image: radial-gradient(ellipse at 50% 50%, #000 40%, transparent 80%);
        }
        .cn-login__form::before {
            content: "";
            position: absolute;
            top: -160px;
            right: -160px;
            width: 360px;
            height: 360px;
            border-radius: 50%;
            background: var(--cn-orange);
            opacity: 0.1;
            filter: blur(40px);
        }

        .cn-login__form-inner {
            position: relative;
            z-index: 1;
            width: 100%;
            max-width: 420px;
            background: #fff;
            border: 1px solid rgba(31, 68, 134, 0.08);
            border-radius: 16px;
            padding: 40px 36px 32px;
            box-shadow:
                0 1px 2px rgba(15, 23, 41, 0.04),
                0 12px 32px -8px rgba(15, 23, 41, 0.08);
        }

        .cn-login__mark {
            width: 44px;
            height: 44px;
            border-radius: 12px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, var(--cn-navy) 0%, var(--cn-navy-700) 100%);
            color: #fff;
            box-shadow: 0 6px 16px -4px rgba(31, 68, 134, 0.5);
            margin-bottom: 18px;
            position: relative;
        }
        .cn-login__mark::after {
            content: "";
            position: absolute;
            right: -4px;
            bottom: -4px;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background: var(--cn-orange);
            border: 2px solid #fff;
        }

        .cn-login__head h2 {
            font-weight: 800;
            font-size: 26px !important;
            letter-spacing: -0.02em;
            margin: 0 0 6px;
            color: var(--cn-fg-1);
        }
        .cn-login__head p {
            font-size: 14px;
            line-height: 1.5;
            color: var(--cn-fg-3);
            margin: 0 0 26px;
        }

        .cn-field {
            display: block;
            margin-bottom: 16px;
        }
        .cn-field.cn-field__error {
            margin-bottom: 0;
        }

        .cn-field__label {
            display: flex;
            justify-content: space-between;
            align-items: baseline;
            font-size: 12.5px;
            font-weight: 600;
            color: var(--cn-fg-2);
            letter-spacing: 0.01em;
            text-transform: uppercase;
            margin-bottom: 7px;
        }
        .cn-field__link {
            font-size: 12px;
            font-weight: 600;
            color: var(--cn-navy);
            text-decoration: none;
            text-transform: none;
            letter-spacing: 0;
        }
        .cn-field__link:hover {
            color: var(--cn-orange-600);
            text-decoration: underline;
        }

        .cn-field__wrap {
            position: relative;
            display: block;
        }
        :host ::ng-deep .cn-field__icon svg {
            position: absolute;
            left: 13px;
            top: 50%;
            transform: translateY(-50%);
            color: var(--cn-fg-4);
            pointer-events: none;
        }
        .cn-field__wrap input {
            width: 100%;
            padding: 13px 13px 13px 38px;
            font-size: 14.5px;
            border: 1px solid var(--cn-border);
            border-radius: 8px;
            background: #fff;
            color: var(--cn-fg-1);
            transition:
                border-color 150ms,
                box-shadow 150ms;
            outline: none;
            box-sizing: border-box;
        }
        .cn-field__wrap input::placeholder {
            color: var(--cn-fg-4);
        }
        .cn-field__wrap input:focus {
            border-color: var(--cn-navy);
            box-shadow: 0 0 0 3px rgba(31, 68, 134, 0.15);
        }
        .cn-field__pwwrap input {
            padding-right: 64px;
        }
        .cn-field__toggle {
            position: absolute;
            right: 6px;
            top: 50%;
            transform: translateY(-50%);
            background: transparent;
            border: 0;
            color: var(--cn-fg-3);
            font: inherit;
            font-size: 12.5px;
            font-weight: 600;
            cursor: pointer;
            padding: 6px 10px;
            border-radius: 4px;
        }
        .cn-field__toggle:hover {
            background: var(--cn-bg-alt);
            color: var(--cn-navy);
        }

        .cn-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            width: 100%;
            padding: 13px 18px;
            margin-top: 8px;
            font-size: 15px;
            font-weight: 600;
            border-radius: 10px;
            border: 1px solid transparent;
            cursor: pointer;
            transition:
                background 150ms,
                transform 80ms,
                box-shadow 150ms;
            box-sizing: border-box;
        }
        .cn-btn:disabled {
            opacity: 0.55;
            cursor: not-allowed;
        }
        .cn-btn:not(:disabled):active {
            transform: translateY(1px);
        }
        .cn-btn--primary {
            background: var(--cn-navy);
            color: #fff;
            box-shadow: 0 4px 12px -2px rgba(31, 68, 134, 0.35);
        }
        .cn-btn--primary:not(:disabled):hover {
            background: var(--cn-navy-700);
            box-shadow: 0 6px 16px -2px rgba(31, 68, 134, 0.45);
        }
        .cn-btn--primary svg {
            transition: transform 150ms;
        }
        .cn-btn--primary:not(:disabled):hover svg {
            transform: translateX(2px);
        }

        .cn-login__forgot {
            display: block;
            width: 100%;
            text-align: right;
            margin: -0.5rem 0 1rem;
            background: none;
            border: 0;
            padding: 0;
            cursor: pointer;
        }

        .cn-login__forgot:disabled {
            opacity: 0.55;
            cursor: not-allowed;
        }

        .cn-login__forgot-result {
            margin: 0 0 1rem;
            font-size: 0.875rem;
        }

        .cn-login__forgot-result--error {
            color: var(--error-message-color, #c00);
        }

        *:focus-visible {
            outline: 2px solid var(--cn-navy);
            outline-offset: 2px;
        }

        :host ::ng-deep s25-ng-loading-inline-static .c-loading__inner {
            stroke: #fff;
        }

        :host ::ng-deep s25-ng-loading-inline-static .s25-text {
            display: none;
        }
    `,
    imports: [S25IconComponent, NgTemplateOutlet, FormField, S25LoadingInlineStaticComponent, FormsModule],
})
export class SeLoginComponent {
    constructor() {
        void MoreMenuUtil.applyDarkModeFromUserPreference();
    }

    readonly showForgotPassword = s25Resource(async () => {
        const [prefs, prefsErr] = await S25Util.Maybe(PreferenceService.getPreferences(["config_email_password"]));
        if (prefsErr) return false;
        return prefs?.config_email_password?.value === "show";
    });

    readonly userModel = signal<{ username: string; password: string }>({ username: "", password: "" });
    readonly loginForm = form(this.userModel, ({ username, password }) => {
        required(username, { message: "Please enter a username" });
        required(password, { message: "Please enter a password" });
    });

    loginError = signal("");
    showPw = signal(false);
    requestingReset = signal(false);
    forgotPasswordResult = signal<{ success: boolean; message: string } | undefined>(undefined);

    @Bind
    async forgotPassword() {
        if (!this.loginForm.username().valid() || this.requestingReset()) return;

        this.loginError.set("");
        this.forgotPasswordResult.set(undefined);
        this.requestingReset.set(true);
        try {
            const username = this.loginForm.username().value();
            this.forgotPasswordResult.set(
                await PasswordResetService.requestReset(username, { originatingApp: "connect25" }),
            );
        } catch (error) {
            this.loginError.set(<string>S25Util.errorText(error));
        } finally {
            this.requestingReset.set(false);
        }
    }

    logIn() {
        if (this.loginForm().invalid() || this.loginForm().submitting()) return;
        this.forgotPasswordResult.set(undefined);
        return submit(this.loginForm, async () => {
            const { username, password } = this.loginForm().value();
            const [_, error] = await S25Util.Maybe(AuthService.authenticate(username, password));

            if (error) {
                this.loginError.set(<string>S25Util.errorText(error));
                return;
            }

            StateService.go(S25States.names.seHomeView, null, null);
        });
    }
}
